import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  IconButton,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonIcon
} from '@mui/icons-material';
import api from '../../services/api';

const TrackmaniaPlayerMappingDialog = ({ open, onClose, mappings, onSave }) => {
  const [localMappings, setLocalMappings] = useState(mappings || []);
  const [ggwpUsers, setGgwpUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Fetch GGWP users when dialog opens
  useEffect(() => {
    if (open) {
      loadUsers();
      setLocalMappings(mappings || []);
    }
  }, [open, mappings]);
  
  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get('/api/users/');
      setGgwpUsers(response.data);
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };
  
  const addMapping = () => {
    setLocalMappings([...localMappings, { pyplanet_login: '', ggwp_user_id: '' }]);
  };
  
  const removeMapping = (index) => {
    setLocalMappings(localMappings.filter((_, i) => i !== index));
  };
  
  const updateMapping = (index, field, value) => {
    const updated = [...localMappings];
    updated[index][field] = value;
    setLocalMappings(updated);
  };
  
  const handleSave = () => {
    // Filter out empty mappings
    const validMappings = localMappings.filter(
      m => m.pyplanet_login && m.ggwp_user_id
    );
    onSave(validMappings);
    onClose();
  };
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon />
          Map PyPlanet Players to GGWP Users
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Alert severity="info" sx={{ mb: 2 }}>
          Map Trackmania PyPlanet player logins to GGWP user accounts. This allows proper point attribution and user identification in rankings.
        </Alert>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <List>
              {localMappings.length === 0 ? (
                <ListItem>
                  <Typography color="text.secondary">
                    No player mappings yet. Click "Add Mapping" to get started.
                  </Typography>
                </ListItem>
              ) : (
                localMappings.map((mapping, index) => (
                  <ListItem key={index} sx={{ gap: 2, px: 0 }}>
                    <TextField
                      label="PyPlanet Login"
                      value={mapping.pyplanet_login}
                      onChange={(e) => updateMapping(index, 'pyplanet_login', e.target.value)}
                      sx={{ flex: 1 }}
                      size="small"
                      placeholder="e.g., player123"
                    />
                    <TextField
                      label="GGWP User"
                      select
                      value={mapping.ggwp_user_id}
                      onChange={(e) => updateMapping(index, 'ggwp_user_id', e.target.value)}
                      sx={{ flex: 1 }}
                      size="small"
                    >
                      <MenuItem value="">Select GGWP User</MenuItem>
                      {ggwpUsers.map(user => (
                        <MenuItem key={user.id || user._id} value={user.id || user._id}>
                          {user.username}
                        </MenuItem>
                      ))}
                    </TextField>
                    <IconButton 
                      onClick={() => removeMapping(index)} 
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItem>
                ))
              )}
            </List>
            <Button 
              startIcon={<AddIcon />} 
              onClick={addMapping}
              variant="outlined"
              fullWidth
              sx={{ mt: 2 }}
            >
              Add Mapping
            </Button>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={loading}>
          Save Mappings
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrackmaniaPlayerMappingDialog;

